import {useParams} from 'react-router-dom'
import cn from 'clsx'
import {parseISO} from 'date-fns'
import {Space, Upload} from 'antd'

import styles from './BorrowerProjectConditions.module.scss'
import {
  Box,
  Text,
  File,
  CalendarDate,
  Button,
  Loader,
  ShieldChekThirdIcon,
} from 'common/components'
import {useUploadFile} from 'common/api'
import {maybePluralize} from 'common/helpers'
import {FileFromServer} from 'common/types'
import {
  useGetProjectConditions,
  useAddProjectConditionDocument,
  useRemoveProjectConditionDocument,
} from '..'

interface ListProps {
  items: any[]
  today?: Date
}

interface ItemProps {
  item: any
  today?: Date
}

interface UploadDocumentProps {
  conditionUuid: string
  projectUuid: string
}

interface RemovableFileProps {
  document: FileFromServer
  conditionUuid: string
  projectUuid: string
}

export default function BorrowerProjectConditions() {
  const params = useParams()
  const {data: conditions} = useGetProjectConditions(params.uuid)

  if (!conditions) return <Loader />

  const currentConditions = conditions.filter(
    (condition: any) => !condition.checkedDate
  )
  const today = new Date()
  const overdueConditionsCount = currentConditions.filter(
    (condition: any) => today > parseISO(condition.date)
  ).length
  const completedConditions = conditions.filter(
    (condition: any) => condition.checkedDate
  )

  return (
    <div className={styles.root}>
      {currentConditions.length ? (
        <div>
          <div>
            <h3 className={cn(styles.title, styles.title_border)}>
              {maybePluralize(currentConditions.length, 'condition')}
              {Boolean(overdueConditionsCount) && (
                <Text
                  as="span"
                  size="md"
                  weight="demiBold"
                  color="redHalf"
                  ml={20}
                >
                  {maybePluralize(overdueConditionsCount, 'overdue condition')}
                </Text>
              )}
            </h3>
          </div>
          <List
            items={currentConditions}
            today={overdueConditionsCount ? today : undefined}
          />
        </div>
      ) : (
        <Box pt={40} pb={40}>
          <Space size={12}>
            <ShieldChekThirdIcon />
            <h3 className={styles.title}>All conditions are completed</h3>
          </Space>
        </Box>
      )}
      {Boolean(completedConditions.length) && (
        <div className={styles.block}>
          <h3 className={styles.subTitle}>Completed conditions</h3>
          <List items={completedConditions} />
        </div>
      )}
    </div>
  )
}

const List = ({items, today}: ListProps) => {
  return (
    <ul>
      {items.map((item) => (
        <Item key={item.uuid} item={item} today={today} />
      ))}
    </ul>
  )
}

const Item = ({item, today}: ItemProps) => {
  const params = useParams()

  const date = parseISO(item.date)
  const isOverdue = today && today > date
  const isChecked = Boolean(item.checkedDate)

  return (
    <li className={styles.item}>
      <CalendarDate date={date} error={isOverdue} />
      <Text cross={isChecked} color={isOverdue ? 'redError' : undefined}>
        {item.description}
      </Text>
      <ul className={styles.files}>
        {item.documents.map((document: FileFromServer) => (
          <li key={document.uuid}>
            {isChecked ? (
              <File
                name={document.name}
                size={document.size}
                url={document.url}
                createdAt={document.createdAt}
              />
            ) : (
              <RemovableFile
                document={document}
                conditionUuid={item.uuid}
                projectUuid={params.uuid}
              />
            )}
          </li>
        ))}
      </ul>
      {/* <Box align="right">
        {!isChecked && (
          <UploadDocument conditionUuid={item.uuid} projectUuid={params.uuid} />
        )}
      </Box> */}
    </li>
  )
}

const RemovableFile = ({
  document,
  conditionUuid,
  projectUuid,
}: RemovableFileProps) => {
  const {mutate: removeDocument, isMutating: isRemoveDocumentMutating} =
    useRemoveProjectConditionDocument(conditionUuid, document.uuid, projectUuid)

  return (
    <File
      name={document.name}
      size={document.size}
      url={document.url}
      createdAt={document.createdAt}
      isRemoveLoading={isRemoveDocumentMutating}
      onRemove={removeDocument}
    />
  )
}

const UploadDocument = ({conditionUuid, projectUuid}: UploadDocumentProps) => {
  const {mutate: addDocument, isMutating: isAddDocumentMutating} =
    useAddProjectConditionDocument(conditionUuid, projectUuid)
  const {mutate: uploadFile, isMutating: isUploadFileMutating} = useUploadFile({
    key: conditionUuid,
    attachFile: (fileUuid) => addDocument(fileUuid),
  })

  const hanldeUpload = ({file}: any) => uploadFile(file)

  const uploadDisabled = isUploadFileMutating || isAddDocumentMutating

  return (
    <Upload
      name="file"
      showUploadList={false}
      disabled={uploadDisabled}
      customRequest={hanldeUpload}
    >
      <Button variant="secondary" loading={uploadDisabled}>
        upload document
      </Button>
    </Upload>
  )
}
